
import React, { useEffect, useState, useCallback } from 'react';
import { PostHistoryItem, PostStatus, ModerationStatus, Connector, AccessInfo, BxUser } from '../types';
import { getPosts, getConnectors } from '../services/api';
import { 
    Plus, RefreshCcw, ChevronLeft, 
    ChevronRight, Clock, CheckCircle2, XCircle, 
    Filter, ChevronDown, Layers, Hash, Loader2, Calendar, User, ArrowUpRight
} from 'lucide-react';
import { PostWizard } from './PostWizard';
import { PostDetails } from './PostDetails';

interface DashboardProps {
    access: AccessInfo | null;
    currentUser: BxUser | null;
}

export const Dashboard: React.FC<DashboardProps> = ({ access, currentUser }) => {
    const [posts, setPosts] = useState<PostHistoryItem[]>([]);
    const [meta, setMeta] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [connectors, setConnectors] = useState<Connector[]>([]);
    
    const [view, setView] = useState<'list' | 'create' | 'details'>('list');
    const [selectedPostId, setSelectedPostId] = useState<string | null>(null);

    const [statusFilter, setStatusFilter] = useState<PostStatus | 'all'>('all');
    const [moderationFilter, setModerationFilter] = useState<ModerationStatus | 'all'>('all');
    const [page, setPage] = useState(1);
    const limit = 8; // Уменьшим лимит, так как карточки крупнее

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [postsRes, connRes] = await Promise.all([
                getPosts({ status: statusFilter, moderationStatus: moderationFilter, page, limit }),
                getConnectors()
            ]);
            setPosts(postsRes.result);
            setMeta(postsRes.meta);
            setConnectors(connRes.result);
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    }, [statusFilter, moderationFilter, page, limit]);

    useEffect(() => { loadData(); }, [loadData]);

    const getStatusInfo = (status: PostStatus, moderation: ModerationStatus) => {
        if (status === 'sent') return { label: 'Опубликовано', color: 'bg-emerald-500', text: 'text-emerald-600', icon: CheckCircle2 };
        if (status === 'failed') return { label: 'Ошибка', color: 'bg-rose-500', text: 'text-rose-600', icon: XCircle };
        
        if (moderation === 'approved') return { label: 'Одобрено', color: 'bg-blue-500', text: 'text-blue-600', icon: CheckCircle2 };
        if (moderation === 'rejected') return { label: 'Отклонено', color: 'bg-rose-500', text: 'text-rose-600', icon: XCircle };
        return { label: 'На проверке', color: 'bg-amber-500', text: 'text-amber-600', icon: Clock };
    };

    if (view === 'create') return <PostWizard onClose={() => setView('list')} onSuccess={() => { setView('list'); loadData(); }} />;
    if (view === 'details' && selectedPostId) return <PostDetails postId={selectedPostId} access={access} currentUser={currentUser} onBack={() => { setSelectedPostId(null); setView('list'); loadData(); }} />;

    return (
        <div className="space-y-10 animate-in fade-in duration-700 max-w-7xl mx-auto pb-20">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8 bg-white p-8 rounded-[3rem] border border-slate-200 shadow-sm">
                <div>
                    <h2 className="text-4xl font-black text-slate-900 tracking-tighter leading-none mb-3">Лента публикаций</h2>
                    <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${loading ? 'bg-slate-300' : 'bg-blue-600 animate-pulse'}`}></div>
                        <p className="text-slate-400 text-xs font-black uppercase tracking-[0.2em]">
                            {loading ? 'Обновление...' : `Всего в базе: ${meta?.total || 0}`}
                        </p>
                    </div>
                </div>
                {access?.canCreatePosts && (
                    <button 
                        onClick={() => setView('create')} 
                        disabled={loading}
                        className="group flex items-center gap-4 px-8 py-5 bg-blue-600 text-white rounded-[2rem] font-black text-sm uppercase tracking-widest hover:bg-blue-700 transition-all shadow-2xl shadow-blue-600/30 active:scale-95 disabled:opacity-50"
                    >
                        <Plus className="w-6 h-6 group-hover:rotate-90 transition-transform" /> 
                        Создать публикацию
                    </button>
                )}
            </div>

            {/* Filters Bar */}
            <div className="bg-white/50 backdrop-blur-md p-5 rounded-[2.5rem] border border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-6 px-8">
                <div className="flex flex-wrap gap-4 items-center">
                    <div className="flex items-center gap-3 mr-2">
                        <Filter className="w-5 h-5 text-slate-400" />
                        <span className="text-xs font-black text-slate-500 uppercase tracking-widest">Фильтры</span>
                    </div>

                    <div className="relative group">
                        <select 
                            value={statusFilter} 
                            disabled={loading}
                            onChange={(e) => { setPage(1); setStatusFilter(e.target.value as any); }} 
                            className="bg-white border border-slate-200 rounded-2xl pl-5 pr-12 py-3.5 text-xs font-black text-slate-700 outline-none hover:border-blue-400 focus:ring-4 focus:ring-blue-100 transition-all appearance-none cursor-pointer min-w-[180px] shadow-sm disabled:opacity-50"
                        >
                            <option value="all">Все статусы</option>
                            <option value="scheduled">В очереди</option>
                            <option value="sent">Отправлены</option>
                            <option value="failed">Ошибки</option>
                        </select>
                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none group-hover:text-blue-500 transition-colors" />
                    </div>

                    <div className="relative group">
                        <select 
                            value={moderationFilter} 
                            disabled={loading}
                            onChange={(e) => { setPage(1); setModerationFilter(e.target.value as any); }} 
                            className="bg-white border border-slate-200 rounded-2xl pl-5 pr-12 py-3.5 text-xs font-black text-slate-700 outline-none hover:border-indigo-400 focus:ring-4 focus:ring-indigo-100 transition-all appearance-none cursor-pointer min-w-[180px] shadow-sm disabled:opacity-50"
                        >
                            <option value="all">Модерация: Все</option>
                            <option value="pending">Ожидают</option>
                            <option value="approved">Одобрены</option>
                            <option value="rejected">Отклонены</option>
                        </select>
                        <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none group-hover:text-indigo-500 transition-colors" />
                    </div>
                </div>

                <button 
                    onClick={() => loadData()} 
                    disabled={loading}
                    className="flex items-center gap-3 px-6 py-3.5 text-slate-500 hover:text-blue-600 bg-white border border-slate-200 rounded-[1.5rem] transition-all active:scale-95 font-black text-xs uppercase tracking-widest shadow-sm disabled:opacity-50"
                >
                    <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    Обновить данные
                </button>
            </div>

            {/* Cards Grid */}
            <div className="relative min-h-[400px]">
                {loading ? (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 animate-in fade-in duration-300">
                        {[1, 2, 3, 4].map(i => (
                            <div key={i} className="bg-white rounded-[3rem] border border-slate-100 p-8 shadow-sm h-full min-h-[380px] flex flex-col">
                                {/* Header Skeleton */}
                                <div className="flex justify-between items-start mb-8 animate-pulse">
                                    <div className="flex items-center gap-4">
                                        <div className="w-20 h-10 bg-slate-100 rounded-2xl" />
                                        <div className="space-y-2">
                                            <div className="w-16 h-3 bg-slate-100 rounded-full" />
                                            <div className="w-24 h-3 bg-slate-100 rounded-full" />
                                        </div>
                                    </div>
                                    <div className="w-24 h-8 bg-slate-100 rounded-2xl" />
                                </div>

                                {/* Body Skeleton */}
                                <div className="grid grid-cols-2 gap-6 mb-10 animate-pulse">
                                    <div className="h-32 bg-slate-50 rounded-[2rem] border border-slate-100" />
                                    <div className="h-32 bg-slate-50 rounded-[2rem] border border-slate-100" />
                                </div>

                                {/* Footer Skeleton */}
                                <div className="mt-auto pt-8 border-t border-slate-50 flex justify-between items-center animate-pulse">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 bg-slate-100 rounded-2xl" />
                                        <div className="space-y-2">
                                            <div className="w-20 h-3 bg-slate-100 rounded-full" />
                                            <div className="w-16 h-2 bg-slate-100 rounded-full" />
                                        </div>
                                    </div>
                                    <div className="w-12 h-12 bg-slate-50 rounded-2xl" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : posts.length === 0 ? (
                    <div className="bg-white rounded-[3rem] border border-slate-200 py-32 text-center shadow-sm">
                        <div className="w-24 h-24 bg-slate-50 rounded-[2.5rem] flex items-center justify-center mx-auto mb-6 shadow-inner">
                            <Layers className="w-12 h-12 text-slate-200" />
                        </div>
                        <h4 className="text-xl font-black text-slate-800 uppercase tracking-widest">Ничего не найдено</h4>
                        <p className="text-sm font-bold text-slate-400 mt-2 uppercase">Измените параметры фильтрации</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
                        {posts.map(post => {
                            const st = getStatusInfo(post.status, post.moderationStatus);
                            return (
                                <div 
                                    key={post.id} 
                                    onClick={() => (setSelectedPostId(post.id), setView('details'))} 
                                    className="group relative bg-white rounded-[3rem] border border-slate-200 p-8 shadow-xl shadow-slate-200/50 hover:shadow-2xl hover:shadow-blue-500/10 hover:-translate-y-2 transition-all duration-500 cursor-pointer overflow-hidden"
                                >
                                    {/* Card Header: ID & Date */}
                                    <div className="flex justify-between items-start mb-8">
                                        <div className="flex items-center gap-4">
                                            <div className="px-5 py-2.5 bg-slate-100 text-slate-400 group-hover:bg-blue-600 group-hover:text-white rounded-2xl text-lg font-black transition-all duration-500 shadow-inner">
                                                #{post.id.split('-')[0].toUpperCase()}
                                            </div>
                                            <div>
                                                <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Создано</div>
                                                <div className="text-xs font-bold text-slate-900">{new Date(post.createdAt).toLocaleString('ru-RU')}</div>
                                            </div>
                                        </div>
                                        <div className={`px-5 py-2.5 ${st.color} text-white rounded-2xl text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg shadow-current/20`}>
                                            <st.icon className="w-4 h-4" />
                                            {st.label}
                                        </div>
                                    </div>

                                    {/* Card Body: Channels & Scheduled Time */}
                                    <div className="grid grid-cols-2 gap-6 mb-10">
                                        <div className="bg-slate-50 rounded-[2rem] p-5 border border-slate-100 shadow-inner">
                                            <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-4">Каналы связи</div>
                                            <div className="flex flex-wrap gap-2.5">
                                                {Array.from(new Set(post.targets.map(t => t.socialNetworkId))).map((netId, i) => (
                                                    <div key={i} className="w-12 h-12 rounded-2xl bg-white border border-slate-100 p-2 shadow-sm group-hover:scale-110 transition-transform">
                                                        <img src={connectors.find(c => c.id === netId)?.logoUrl} className="w-full h-full object-contain" alt="" />
                                                    </div>
                                                ))}
                                                {post.targets.length === 0 && <span className="text-xs font-bold text-slate-300 italic">Не выбраны</span>}
                                            </div>
                                        </div>

                                        <div className="bg-slate-50 rounded-[2rem] p-5 border border-slate-100 shadow-inner">
                                            <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-4">Планирование</div>
                                            {post.scheduledAt ? (
                                                <div className="flex items-center gap-4">
                                                    <div className="w-12 h-12 bg-indigo-100 text-indigo-600 rounded-2xl flex items-center justify-center shadow-sm">
                                                        <Clock className="w-6 h-6" />
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-black text-indigo-700 leading-none mb-1">
                                                            {new Date(post.scheduledAt).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short' })}
                                                        </div>
                                                        <div className="text-[10px] font-bold text-slate-400 uppercase">
                                                            {new Date(post.scheduledAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-4 opacity-50">
                                                    <div className="w-12 h-12 bg-slate-200 text-slate-400 rounded-2xl flex items-center justify-center">
                                                        <Plus className="w-6 h-6 rotate-45" />
                                                    </div>
                                                    <div className="text-[10px] font-black text-slate-400 uppercase">Мгновенно</div>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Card Footer: Participants */}
                                    <div className="flex items-center justify-between pt-8 border-t border-slate-100">
                                        <div className="flex items-center gap-8">
                                            {/* Creator */}
                                            <div className="flex items-center gap-3">
                                                <div className="relative">
                                                    <img 
                                                        src={post.creator?.photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(post.creator?.name || 'A')}&background=f1f5f9&color=64748b`} 
                                                        className="w-12 h-12 rounded-2xl border-2 border-white shadow-md object-cover" 
                                                        alt="" 
                                                    />
                                                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-600 rounded-lg border-2 border-white flex items-center justify-center shadow-sm">
                                                        <User className="w-3 h-3 text-white" />
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="text-xs font-black text-slate-800 leading-none mb-1 truncate max-w-[120px]">{post.creator?.name || 'Система'}</div>
                                                    <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Автор</div>
                                                </div>
                                            </div>

                                            {/* Moderator */}
                                            {post.assignedModerator && (
                                                <div className="flex items-center gap-3 animate-in slide-in-from-left-2 duration-500">
                                                    <div className="relative">
                                                        <img 
                                                            src={post.assignedModerator?.photo || `https://ui-avatars.com/api/?name=${encodeURIComponent(post.assignedModerator?.name || 'M')}&background=eef2ff&color=4f46e5`} 
                                                            className="w-12 h-12 rounded-2xl border-2 border-white shadow-md object-cover" 
                                                            alt="" 
                                                        />
                                                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-indigo-600 rounded-lg border-2 border-white flex items-center justify-center shadow-sm">
                                                            <CheckCircle2 className="w-3 h-3 text-white" />
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="text-xs font-black text-indigo-700 leading-none mb-1 truncate max-w-[120px]">{post.assignedModerator?.name}</div>
                                                        <div className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Модератор</div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="p-3 bg-slate-50 text-slate-400 group-hover:bg-blue-50 group-hover:text-blue-600 rounded-2xl transition-all shadow-sm">
                                            <ArrowUpRight className="w-6 h-6" />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Pagination Controls */}
            {meta && meta.pages > 1 && (
                <div className="flex items-center justify-center gap-6 pt-10">
                    <button 
                        onClick={() => setPage(prev => Math.max(1, prev - 1))}
                        disabled={meta.page === 1 || loading}
                        className="w-16 h-16 rounded-[2rem] border border-slate-200 bg-white text-slate-600 hover:bg-blue-600 hover:text-white hover:border-blue-600 disabled:opacity-20 transition-all flex items-center justify-center shadow-xl active:scale-90"
                    >
                        <ChevronLeft className="w-8 h-8" />
                    </button>
                    
                    <div className="flex items-center gap-3">
                        {Array.from({ length: meta.pages }).map((_, i) => (
                            <button
                                key={i}
                                onClick={() => setPage(i + 1)}
                                className={`w-3 h-3 rounded-full transition-all ${meta.page === i + 1 ? 'w-10 bg-blue-600 shadow-lg shadow-blue-200' : 'bg-slate-200 hover:bg-slate-300'}`}
                            />
                        ))}
                    </div>

                    <button 
                        onClick={() => setPage(prev => Math.min(meta.pages, prev + 1))}
                        disabled={meta.page === meta.pages || loading}
                        className="w-16 h-16 rounded-[2rem] border border-slate-200 bg-white text-slate-600 hover:bg-blue-600 hover:text-white hover:border-blue-600 disabled:opacity-20 transition-all flex items-center justify-center shadow-xl active:scale-90"
                    >
                        <ChevronRight className="w-8 h-8" />
                    </button>
                </div>
            )}
        </div>
    );
};
